import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import FallbackService from './FallbackService';

/**
 * FeatureToggleManager - Core LaunchDarkly Integration with Direct API Fallback
 * 
 * Provides centralized feature flag management with real-time updates and
 * automatic fallback to Parameter Store API when LaunchDarkly is unavailable.
 * Implements direct API calls without caching for simplified maintenance.
 */
class FeatureToggleManager {
  constructor() {
    this.ldClient = null;
    this.featureToggles = {};
    this.isInitialized = false;
    this.isLoading = false;
    this.initPromise = null;
    this.user = null;
    
    // Event system for real-time component updates
    this.eventListeners = new Set();
    this.updateTimeout = null;
    
    // Fallback system properties
    this.isUsingFallback = false;
    this.ldReconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectInterval = null;
    this.fallbackCache = {};
    this.lastFallbackFetch = null;
  }

  async initialize(clientSideId, user = null) {
    if (this.isInitialized) {
      return this.featureToggles;
    }

    if (this.isLoading) {
      return this.initPromise;
    }

    if (!clientSideId) {
      throw new Error('LaunchDarkly client-side ID is required for initialization');
    }

    this.isLoading = true;
    this.initPromise = this._initializeWithFallback(clientSideId, user);
    
    try {
      await this.initPromise;
      this.isInitialized = true;
    } finally {
      this.isLoading = false;
    }

    return this.featureToggles;
  }

  async _initializeWithFallback(clientSideId, user) {
    const startTime = performance.now();
    
    try {
      // Attempt LaunchDarkly initialization first
      await this._initializeLaunchDarkly(clientSideId, user);
      
      const duration = performance.now() - startTime;
      
    } catch (error) {
      const ldDuration = performance.now() - startTime;

      // Attempt to use Redux store-cached fallback values
      await this._initializeFallback();
    }
  }

  async _initializeLaunchDarkly(clientSideId, user) {
    try {
      // Store configuration for reconnection attempts
      this.clientSideId = clientSideId;
      this.user = user || {
        key: 'anonymous-user',
        anonymous: true
      };

      // Initialize LaunchDarkly client
      this.ldClient = LaunchDarkly.initialize(clientSideId, this.user);
      
      // Wait for client to be ready (with timeout)
      await this.ldClient.waitForInitialization();
      
      // Get all flag values
      const allFlags = this.ldClient.allFlags();
      
      // Filter only keys that start with "cpm-" to maintain existing behavior
      this.featureToggles = Object.keys(allFlags)
        .filter(key => key.startsWith('cpm-'))
        .reduce((filteredToggles, key) => {
          filteredToggles[key] = allFlags[key];
          return filteredToggles;
        }, {});
      
      // Set up event listeners for flag changes
      this._setupFlagChangeListeners();
      
      // Update fallback status
      this.isUsingFallback = false;
        
      return this.featureToggles;
    } catch (error) {
      throw error; // Re-throw to trigger fallback in _initializeWithFallback
    }
  }

  /**
   * Initialize fallback mechanism using direct API calls
   */
  async _initializeFallback() {
    
    try {
      const startTime = performance.now();
      const response = await FallbackService.fetchFallbackValues();
      const duration = performance.now() - startTime;
      
      // Update local state with fallback flags
      this.featureToggles = response.flags;
      this.fallbackCache = response.flags;
      this.lastFallbackFetch = response.timestamp;
      this.isUsingFallback = true;
      
      
      // Notify components about the update
      this._notifyListeners();
      
      // Start monitoring for LaunchDarkly recovery
      this._startLdReconnectionMonitoring();
      
      return this.featureToggles;
      
    } catch (error) {
      
      // Set fallback mode even if API fails - use empty flags
      this.isUsingFallback = true;
      this.featureToggles = {};
      
      // Start monitoring for LaunchDarkly recovery
      this._startLdReconnectionMonitoring();
      
      throw new Error(`Both LaunchDarkly and fallback initialization failed: ${error.message}`);
    }
  }

  /**
   * Start monitoring for LaunchDarkly reconnection
   */
  _startLdReconnectionMonitoring() {
    if (this.reconnectInterval) {
      return; // Already monitoring
    }

    this.ldReconnectAttempts = 0;
    this.reconnectInterval = setInterval(() => {
      if (this.ldReconnectAttempts >= this.maxReconnectAttempts) {
        this._stopLdReconnectionMonitoring();
        return;
      }

      this._attemptLdReconnection();
      this.ldReconnectAttempts++;
    }, 30000); // Try every 30 seconds
  }

  /**
   * Attempt to reconnect to LaunchDarkly and perform hot-swap if successful
   */
  async _attemptLdReconnection() {
    if (!this.isUsingFallback || !this.clientSideId || !this.user) {
      return; // Not in fallback mode or missing config
    }

    const startTime = performance.now();
    let testClient = null;
    
    try {
      // Create a new LaunchDarkly client with original configuration
      testClient = LaunchDarkly.initialize(this.clientSideId, this.user);
      
      // Wait for initialization with timeout
      await Promise.race([
        testClient.waitForInitialization(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Initialization timeout')), 5000)
        )
      ]);
      
      // Success - LaunchDarkly is back online, perform hot-swap
      await this._completeHotSwap(testClient);
      
      const duration = performance.now() - startTime;
      
    } catch (error) {
      // Reconnection failed, clean up test client and continue with fallback
      if (testClient) {
        try {
          testClient.close();
        } catch (closeError) {
          // Ignore close errors
        }
      }
      
      const duration = performance.now() - startTime;
      // Continue using fallback
    }
  }

  /**
   * Complete hot-swap from fallback to LaunchDarkly
   */
  async _completeHotSwap(newLdClient) {
    if (!this.isUsingFallback) {
      return; // Already using LaunchDarkly
    }

    try {
      // Close old client if exists
      if (this.ldClient) {
        this.ldClient.close();
      }
      
      // Switch to new client
      this.ldClient = newLdClient;
      
      // Stop reconnection monitoring
      this._stopLdReconnectionMonitoring();
      
      // Reset reconnection attempts
      this.ldReconnectAttempts = 0;
      
      // Clear fallback cache
      this.fallbackCache = {};
      this.lastFallbackFetch = null;
      
      // Update status before loading flags
      this.isUsingFallback = false;
      
      // Load fresh flags from LaunchDarkly
      await this._loadFlagsFromLaunchDarkly();
      
      // Set up event listeners for new client
      this._setupFlagChangeListeners();
      
      // Notify all listeners about the hot-swap
      this._notifyListeners(this.featureToggles);
      
    } catch (error) {
      // Hot-swap failed, revert to fallback
      this.isUsingFallback = true;
      if (newLdClient) {
        newLdClient.close();
      }
      this.ldClient = null;
      this._startLdReconnectionMonitoring();
      throw error;
    }
  }

  /**
   * Load flags from LaunchDarkly client
   */
  async _loadFlagsFromLaunchDarkly() {
    if (!this.ldClient) {
      throw new Error('LaunchDarkly client not available');
    }
    
    const allFlags = this.ldClient.allFlags();
    
    // Filter only keys that start with "cpm-" to maintain existing behavior
    this.featureToggles = Object.keys(allFlags)
      .filter(key => key.startsWith('cpm-'))
      .reduce((filteredToggles, key) => {
        filteredToggles[key] = allFlags[key];
        return filteredToggles;
      }, {});
  }

  /**
   * Stop LaunchDarkly reconnection monitoring
   */
  _stopLdReconnectionMonitoring() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  _setupFlagChangeListeners() {
    if (!this.ldClient) return;

    this.ldClient.on('change', (changes) => {
      const changedFlags = {};
      let cpmFlagsChanged = false;
      
      // Update cached values for changed flags
      Object.keys(changes).forEach(flagKey => {
        if (flagKey.startsWith('cpm-')) {
          this.featureToggles[flagKey] = changes[flagKey].current;
          changedFlags[flagKey] = changes[flagKey].current;
          cpmFlagsChanged = true;
        }
      });
      
      // If we were using fallback and LD is now working, perform hot-swap
      if (this.isUsingFallback && cpmFlagsChanged) {
        
        // Switch back to LaunchDarkly
        this.isUsingFallback = false;
        this._stopLdReconnectionMonitoring();
        
      }
      
      // Notify components about flag changes without page refresh
      if (cpmFlagsChanged) {
        this._notifyListeners(changedFlags);
      }
    });
  }

  // Notify all registered listeners about flag changes
  _notifyListeners(changedFlags = {}) {
    // Clear existing timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    // Debounce notifications to avoid spam
    this.updateTimeout = setTimeout(() => {
      this.eventListeners.forEach(listener => {
        try {
          listener(changedFlags);
        } catch (error) {
          // Silently handle error to prevent disruption
        }
      });
      
      // Dispatch custom DOM event for any components listening
      window.dispatchEvent(new CustomEvent('featureFlagsChanged', {
        detail: changedFlags
      }));
    }, 100);
  }

  isFeatureEnabled(featureName) {
    if (!this.isInitialized) {
      return false;
    }
    
    // Use LaunchDarkly client for real-time evaluation when available
    if (this.ldClient && !this.isUsingFallback) {
      return this.ldClient.variation(featureName, false);
    }
    
    // Fallback to cached value (from Redux store or local cache)
    return this.featureToggles[featureName] === true;
  }

  /**
   * Get the raw value of a feature flag (supports non-boolean types).
   * @param {string} flagKey - The flag key to look up (e.g. 'cpm-max-file-upload-size')
   * @param {*} defaultValue - Value returned when the flag is missing or the system is not initialized
   * @returns {*} The flag value from LaunchDarkly, the fallback cache, or the defaultValue
   */
  getFeatureValue(flagKey, defaultValue) {
    if (!this.isInitialized) {
      return defaultValue;
    }

    // Use LaunchDarkly client for real-time evaluation when available
    if (this.ldClient && !this.isUsingFallback) {
      return this.ldClient.variation(flagKey, defaultValue);
    }

    // Fallback to cached value; use nullish coalescing so falsy values (0, "", false) are preserved
    return this.featureToggles[flagKey] ?? defaultValue;
  }

  // Method to identify a user (useful for targeting)
  identify(user) {
    if (!this.ldClient) {
      return Promise.resolve();
    }
    
    this.user = user;
    return this.ldClient.identify(user);
  }

  // Safe method to update user context with error handling
  async safeIdentifyUser(user) {
    if (!this.isInitialized) {
      return false;
    }

    try {
      await this.identify(user);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Method to close the LaunchDarkly client and cleanup fallback resources
  close() {
    if (this.ldClient) {
      this.ldClient.close();
      this.ldClient = null;
    }
    this.isInitialized = false;
    this.isLoading = false;
    this.initPromise = null;
    this.featureToggles = {};
    
    // Clean up fallback system
    this.isUsingFallback = false;
    this.ldReconnectAttempts = 0;
    this._stopLdReconnectionMonitoring();
    
    // Clean up event listeners
    this.eventListeners.clear();
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  // Utility: check multiple flags at once
  areFeaturesEnabled(featureNames) {
    const results = {};
    featureNames.forEach(featureName => {
      results[featureName] = this.isFeatureEnabled(featureName);
    });
    return results;
  }

  // Get fallback status information (for monitoring/dashboard)
  getFallbackStatus() {
    try {
      const status = {
        isUsingFallback: this.isUsingFallback,
        lastFallbackFetch: this.lastFallbackFetch,
        fallbackFlagCount: this.isUsingFallback ? Object.keys(this.fallbackCache || {}).length : 0,
        ldReconnectAttempts: this.ldReconnectAttempts,
        isMonitoringReconnection: !!this.reconnectInterval,
        isInitialized: this.isInitialized,
        flagCount: Object.keys(this.featureToggles).length,
        ldClientConnected: this.ldClient ? true : false,
        source: this.isUsingFallback ? 'Parameter Store API' : 'LaunchDarkly'
      };

      return status;
    } catch (error) {
      // Return minimal status on error
      return {
        isUsingFallback: this.isUsingFallback,
        error: error.message
      };
    }
  }

  // Subscribe to flag changes for reactive updates
  subscribe(callback) {
    this.eventListeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.eventListeners.delete(callback);
    };
  }

  // Force component updates for specific components
  forceComponentUpdate() {
    // Trigger a small DOM change to force React re-render detection
    const event = new Event('resize');
    window.dispatchEvent(event);
  }

  // Optional: Method to manually refresh if needed
  async refresh(clientSideId, user) {
    this.close();
    return this.initialize(clientSideId, user || this.user);
  }

  // Get the LaunchDarkly client instance (for advanced usage)
  getClient() {
    return this.ldClient;
  }

  // Force refresh of fallback flags from API
  async refreshFallbackFlags() {
    try {
      const response = await FallbackService.fetchFallbackValues();
      this.fallbackCache = response.flags;
      this.lastFallbackFetch = response.timestamp;
      
      if (this.isUsingFallback) {
        // Update current flags if in fallback mode
        this.featureToggles = response.flags;
        this._notifyListeners(response.flags);
      }
      
      return response.flags;
    } catch (error) {
      throw error;
    }
  }
}

// Create singleton instance
const featureToggleManager = new FeatureToggleManager();

export default featureToggleManager;

// Export core functions used by components and the application
export const initializeFeatureToggles = (clientSideId, user) => featureToggleManager.initialize(clientSideId, user);
export const isFeatureEnabled = (featureName) => featureToggleManager.isFeatureEnabled(featureName);
export const getFeatureValue = (flagKey, defaultValue) => featureToggleManager.getFeatureValue(flagKey, defaultValue);
export const areFeaturesEnabled = (featureNames) => featureToggleManager.areFeaturesEnabled(featureNames);
export const safeIdentifyUser = (user) => featureToggleManager.safeIdentifyUser(user);
export const subscribeToFlagChanges = (callback) => featureToggleManager.subscribe(callback);
export const refreshFallbackFlags = () => featureToggleManager.refreshFallbackFlags();
